import * as THREE from 'three';
import { SparkControls } from '@sparkjsdev/spark';
import { CAMERA, CONTROLS } from '../config.js';

/**
 * 内見用のカメラ操作。
 * - 自由操作（ドラッグで見回し、ホイール/ピンチで前後）
 * - 部屋ボタンによる視点ジャンプ（アニメーション中は手動操作を止める）
 */
export function createCameraRig(camera, canvas) {
  const controls = new SparkControls({ canvas });
  tuneControls(controls);

  /** アニメーション中の状態。null なら手動操作中 */
  let flight = null;
  /** マップ表示などで別の仕組みがカメラを動かしている間は true */
  let suspended = false;

  function moveTo(viewpoint, { instant = false } = {}) {
    const target = toPose(viewpoint);
    if (instant) {
      camera.position.copy(target.position);
      camera.quaternion.copy(target.quaternion);
      flight = null;
      return;
    }
    flight = {
      from: { position: camera.position.clone(), quaternion: camera.quaternion.clone() },
      to: target,
      startedAt: performance.now(),
    };
  }

  function update() {
    // SparkControls は update() を呼んだ間隔から deltaTime を計算する。
    // 視点移動中に呼ぶのを止めると、再開した1フレームに移動時間ぶんの
    // 操作量がまとめて適用され、カメラが物件の外まで飛んでしまう。
    // そのため入力の有効・無効を切り替えつつ、update() は毎フレーム呼ぶ。
    setInputEnabled(flight === null && !suspended);
    controls.update(camera);
    if (suspended) return;

    if (flight === null) return;

    const t = (performance.now() - flight.startedAt) / CAMERA.transitionMs;
    if (t >= 1) {
      camera.position.copy(flight.to.position);
      camera.quaternion.copy(flight.to.quaternion);
      flight = null;
      return;
    }
    const e = easeInOutCubic(t);
    camera.position.lerpVectors(flight.from.position, flight.to.position, e);
    camera.quaternion.slerpQuaternions(flight.from.quaternion, flight.to.quaternion, e);
  }

  /**
   * 視点移動中は手動操作を受け付けない。
   * 慣性が残っていると移動完了直後に勝手に動き出すので、そこも毎回消す。
   */
  function setInputEnabled(enabled) {
    const { fpsMovement, pointerControls } = controls;
    fpsMovement.enable = enabled;
    pointerControls.enable = enabled;
    if (!enabled) {
      pointerControls.moveVelocity.set(0, 0, 0);
      pointerControls.rotateVelocity.set(0, 0, 0);
    }
  }

  /** ドラッグなどで手動操作に戻したいときに視点移動を打ち切る */
  function cancelFlight() {
    flight = null;
    setInputEnabled(true);
  }

  /** 手動操作と視点移動を止める。update() は deltaTime のために呼び続けてよい */
  function setSuspended(next) {
    suspended = Boolean(next);
    if (suspended) flight = null;
  }

  return { controls, moveTo, update, cancelFlight, setSuspended, get isFlying() { return flight !== null; } };
}

function tuneControls(controls) {
  const { fpsMovement, pointerControls } = controls;
  const { keyboard, pointer } = CONTROLS;

  fpsMovement.moveSpeed = keyboard.moveSpeed;
  fpsMovement.rotateSpeed = keyboard.rotateSpeed;

  pointerControls.rotateSpeed = pointer.rotateSpeed;
  pointerControls.slideSpeed = pointer.slideSpeed;
  // ホイールはズーム（視野角）に割り当てるので、Spark の「ホイールで前後移動」は使わない
  pointerControls.scrollSpeed = 0;
  pointerControls.moveInertia = pointer.moveInertia;
  pointerControls.rotateInertia = pointer.rotateInertia;
}

/** viewpoint（position + lookAt）を position + quaternion に変換する */
function toPose(viewpoint) {
  const position = new THREE.Vector3().fromArray(viewpoint.position);
  const lookAt = new THREE.Vector3().fromArray(viewpoint.lookAt);
  const matrix = new THREE.Matrix4().lookAt(position, lookAt, UP);
  return { position, quaternion: new THREE.Quaternion().setFromRotationMatrix(matrix) };
}

const UP = Object.freeze(new THREE.Vector3(0, 1, 0));

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}
